import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { TreeSelection } from '../../next/CustomDash/filters/types/filters';

export type GeographyMode = 'msa' | 'administrative';

// Duplicated values to avoid circular dependency with filter types
export const UserPreferenceControlledFilterIds: ReadonlyArray<string> = [
  'CONTINGENT_WORKERS',
  'DATA_ADJUSTMENTS',
  'ROLE_TAXONOMY',
  'SKILL_TAXONOMY',
];

export enum PreferenceScope {
  GLOBAL = 'GLOBAL',
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY = 'COMPANY',
  GEOGRAPHY = 'GEOGRAPHY',
  ROLE = 'ROLE',
}

export enum UserPreferenceKey {
  ContingentWorkers = 'contingentWorkers',
  Taxonomies = 'taxonomies',
  DataAdjustments = 'dataAdjustments',
  DefaultFilters = 'defaultFilters',
  Currency = 'currency',
  Geography = 'geography',
  PageOrder = 'pageOrder',
}

export enum UserPreferenceSubKey {
  RoleTaxonomy = 'roleTaxonomy',
  SkillTaxonomy = 'skillTaxonomy',
}

export type UserPreferences = {
  [UserPreferenceKey.ContingentWorkers]: boolean;
  [UserPreferenceKey.Taxonomies]: {
    role?: 'adaptive' | CustomRoleTaxonomySelection;
    effectiveRoleTaxonomySelection?: CustomRoleTaxonomySelection | null;
    skill?: 'adaptive' | number;
    effectiveSkillTaxonomySelection?: number | null;
  };
  /**
   * Whether to use raw (unadjusted) data.
   * false => Adjusted (default)
   * true  => Unadjusted (raw)
   */
  [UserPreferenceKey.DataAdjustments]: boolean;
  [UserPreferenceKey.Currency]: {
    id?: string;
    code: string;
    rate: number;
  };
  [UserPreferenceKey.DefaultFilters]: TreeSelection[];
  [UserPreferenceKey.Geography]: GeographyMode;
  [UserPreferenceKey.PageOrder]: string[];
};

export type GlobalUserPreferences = Pick<
  UserPreferences,
  UserPreferenceKey.Currency | UserPreferenceKey.Geography
>;

export type ScopedUserPreferences = Omit<
  UserPreferences,
  keyof GlobalUserPreferences
>;

/** List of keys that are considered global-only */
export const GLOBAL_PREFERENCE_KEYS: (keyof UserPreferences)[] = [
  UserPreferenceKey.Currency,
  UserPreferenceKey.Geography,
];

/**
 * Preferences that manage their own persistence (immediate write, own reset
 * UX) and should NOT participate in the modal's draft/diff/bulk-restore flow.
 * Excluded from `calculateVisiblePreferenceDiff`, `saveVisiblePreferencesAsDefault`,
 * and `restoreVisiblePreferencesToDefaults`.
 */
export const SELF_MANAGED_PREFERENCE_KEYS: UserPreferenceKey[] = [
  UserPreferenceKey.PageOrder,
];

/** Scopes where `PageOrder` is editable / cleaned-up. */
export const PAGE_ORDER_SCOPES: PreferenceScope[] = [
  PreferenceScope.COMPANY,
  PreferenceScope.GEOGRAPHY,
  PreferenceScope.ROLE,
];
